import { Link, useParams, Navigate } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import { Button } from "@/components/ui/button";
import { ArrowRight, Check, CheckCircle2 } from "lucide-react";
import { servicesBySlug, services } from "@/data/realEstateServices";
import ServiceTrustStrip from "@/components/ServiceTrustStrip";
import SkylineOverlay from "@/components/brand/SkylineOverlay";
import InlineBookingCTA from "@/components/InlineBookingCTA";

const ServiceLanding = () => {
  const { slug } = useParams<{ slug: string }>();
  const service = slug ? servicesBySlug[slug] : undefined;

  if (!service) return <Navigate to="/services" replace />;

  const url = `https://www.elevateglobalsolutions.net/services/${service.slug}`;

  const breadcrumbSchema = {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    itemListElement: [
      { "@type": "ListItem", position: 1, name: "Home", item: "https://www.elevateglobalsolutions.net/" },
      { "@type": "ListItem", position: 2, name: "Services", item: "https://www.elevateglobalsolutions.net/services" },
      { "@type": "ListItem", position: 3, name: service.title, item: url },
    ],
  };

  const serviceSchema = {
    "@context": "https://schema.org",
    "@type": "Service",
    name: service.title,
    serviceType: service.title,
    provider: {
      "@type": "Organization",
      name: "Elevate Global Solutions",
      url: "https://www.elevateglobalsolutions.net",
    },
    areaServed: { "@type": "Country", name: "United States" },
    description: service.metaDescription,
    url,
  };

  const faqSchema = {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    mainEntity: service.faqs.map((f) => ({
      "@type": "Question",
      name: f.q,
      acceptedAnswer: { "@type": "Answer", text: f.a },
    })),
  };

  const related = service.related.map((s) => servicesBySlug[s]).filter(Boolean);

  return (
    <>
      <Helmet>
        <title>{service.metaTitle}</title>
        <meta name="description" content={service.metaDescription} />
        <meta name="keywords" content={service.keywords.join(", ")} />
        <link rel="canonical" href={url} />
        <meta property="og:title" content={service.metaTitle} />
        <meta property="og:description" content={service.metaDescription} />
        <meta property="og:url" content={url} />
        <meta property="og:type" content="website" />
        <meta property="og:image" content="https://www.elevateglobalsolutions.net/og-default.jpg" />
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:image" content="https://www.elevateglobalsolutions.net/og-default.jpg" />
        <meta name="twitter:title" content={service.metaTitle} />
        <meta name="twitter:description" content={service.metaDescription} />
        <script type="application/ld+json">{JSON.stringify(breadcrumbSchema)}</script>
        <script type="application/ld+json">{JSON.stringify(serviceSchema)}</script>
        <script type="application/ld+json">{JSON.stringify(faqSchema)}</script>
      </Helmet>

      {/* Hero */}
      <section className="relative overflow-hidden bg-primary">
        <div className="absolute inset-0 opacity-10">
          <div className="absolute -right-20 -top-20 h-96 w-96 rounded-full bg-secondary" />
          <div className="absolute -bottom-32 -left-32 h-[500px] w-[500px] rounded-full bg-primary-foreground" />
        </div>
        <SkylineOverlay opacityClass="opacity-[0.06]" />
        <div className="container-narrow relative section-padding py-20 md:py-28">
          <nav className="mb-5 text-xs text-primary-foreground/70">
            <Link to="/" className="hover:text-primary-foreground">Home</Link>
            <span className="mx-2">/</span>
            <Link to="/services" className="hover:text-primary-foreground">Services</Link>
            <span className="mx-2">/</span>
            <span className="text-primary-foreground">{service.shortLabel}</span>
          </nav>
          <span className="inline-flex items-center gap-2 rounded-full border border-primary-foreground/20 bg-primary-foreground/10 px-3 py-1 text-xs font-medium text-primary-foreground/90 backdrop-blur">
            <span className="inline-block h-2 w-2 animate-pulse rounded-full bg-secondary" />
            {service.heroEyebrow}
          </span>
          <h1 className="mt-5 max-w-3xl text-4xl font-extrabold leading-tight text-primary-foreground md:text-5xl lg:text-6xl">
            {service.h1}
          </h1>
          <p className="mt-6 max-w-2xl text-lg text-primary-foreground/85 md:text-xl">{service.heroSubhead}</p>
          <div className="mt-8 flex flex-wrap gap-3">
            <Link to="/book">
              <Button size="lg" className="bg-secondary text-secondary-foreground hover:bg-secondary/90 shadow-lg shadow-secondary/30">
                Book a Free Strategy Call <ArrowRight className="ml-2 h-4 w-4" />
              </Button>
            </Link>
            <Link to="/services">
              <Button size="lg" variant="outline" className="border-primary-foreground bg-primary-foreground text-primary hover:bg-primary-foreground/90">
                All Services
              </Button>
            </Link>
          </div>
        </div>
      </section>

      {/* TRUST STRIP */}
      <ServiceTrustStrip />

      {/* KPIs strip */}
      <section className="border-b border-border bg-card">
        <div className="container-narrow grid grid-cols-2 gap-6 px-4 py-8 md:grid-cols-4 md:px-8 lg:px-16">
          {service.kpis.map((k) => (
            <div key={k.label}>
              <div className="text-2xl font-extrabold text-primary md:text-3xl">{k.value}</div>
              <div className="text-xs uppercase tracking-wider text-muted-foreground">{k.label}</div>
            </div>
          ))}
        </div>
      </section>

      {/* Problem */}
      <section className="section-padding">
        <div className="container-narrow grid gap-12 lg:grid-cols-2">
          <div>
            <h2 className="text-3xl font-bold text-foreground md:text-4xl">{service.problem.headline}</h2>
            <ul className="mt-6 space-y-4">
              {service.problem.points.map((p) => (
                <li key={p} className="flex gap-3 text-muted-foreground">
                  <span className="mt-2 inline-block h-1.5 w-1.5 shrink-0 rounded-full bg-secondary" />
                  <span>{p}</span>
                </li>
              ))}
            </ul>
          </div>
          <div className="rounded-2xl border border-border bg-accent/40 p-8">
            <h3 className="text-xl font-bold text-foreground">{service.solution.headline}</h3>
            <p className="mt-4 text-muted-foreground">{service.solution.body}</p>
            <Link to="/book" className="mt-6 inline-block">
              <Button className="bg-primary text-primary-foreground hover:bg-primary/90">
                Get a tailored plan <ArrowRight className="ml-2 h-4 w-4" />
              </Button>
            </Link>
          </div>
        </div>
      </section>

      {/* What's included */}
      <section className="section-padding bg-accent/40">
        <div className="container-narrow">
          <h2 className="text-3xl font-bold text-foreground md:text-4xl">What's included</h2>
          <div className="mt-8 grid gap-3 sm:grid-cols-2">
            {service.whatsIncluded.map((item) => (
              <div key={item} className="flex items-start gap-3 rounded-lg border border-border bg-card p-4">
                <CheckCircle2 className="mt-0.5 h-5 w-5 shrink-0 text-secondary" />
                <span className="text-sm text-foreground">{item}</span>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Process */}
      <section className="section-padding">
        <div className="container-narrow">
          <h2 className="text-3xl font-bold text-foreground md:text-4xl">How it works</h2>
          <div className="mt-10 grid gap-6 md:grid-cols-2 lg:grid-cols-4">
            {service.process.map((p) => (
              <div key={p.step} className="rounded-xl border border-border bg-card p-6">
                <div className="text-xs font-bold uppercase tracking-wider text-secondary">{p.step}</div>
                <h3 className="mt-2 text-lg font-semibold text-foreground">{p.title}</h3>
                <p className="mt-2 text-sm text-muted-foreground">{p.body}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* FAQ */}
      <section className="section-padding bg-accent/40">
        <div className="container-narrow">
          <h2 className="text-3xl font-bold text-foreground md:text-4xl">Frequently asked questions</h2>
          <div className="mt-8 space-y-4">
            {service.faqs.map((f) => (
              <details key={f.q} className="group rounded-xl border border-border bg-card p-5">
                <summary className="cursor-pointer list-none text-base font-semibold text-foreground">
                  <span className="flex items-center justify-between gap-3">
                    {f.q}
                    <span className="text-secondary transition-transform group-open:rotate-45">+</span>
                  </span>
                </summary>
                <p className="mt-3 text-sm text-muted-foreground">{f.a}</p>
              </details>
            ))}
          </div>
        </div>
      </section>

      {/* Related */}
      {related.length > 0 && (
        <section className="section-padding">
          <div className="container-narrow">
            <h2 className="text-2xl font-bold text-foreground md:text-3xl">Related services</h2>
            <div className="mt-8 grid gap-6 md:grid-cols-3">
              {related.map((r) => (
                <Link key={r.slug} to={`/services/${r.slug}`} className="group rounded-xl border border-border bg-card p-6 transition-colors hover:border-primary">
                  <div className="text-xs uppercase tracking-wider text-secondary">{r.shortLabel}</div>
                  <h3 className="mt-2 text-lg font-semibold text-foreground group-hover:text-primary">{r.title}</h3>
                  <p className="mt-2 line-clamp-2 text-sm text-muted-foreground">{r.metaDescription}</p>
                  <div className="mt-4 inline-flex items-center text-sm font-medium text-primary">
                    Explore <ArrowRight className="ml-1 h-4 w-4" />
                  </div>
                </Link>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* Inline booking + agenda */}
      <InlineBookingCTA serviceLabel={service.shortLabel} />

    </>
  );
};

export { services as allServices };
export default ServiceLanding;
